interface PlayerOptions {
  sampleRate?: number;
  channels?: number;
  bitDepth?: number;
}

export class PCMPlayer {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private nextPlaybackTime = 0;
  private muted = false;
  private volume = 1;
  private channels = 1;
  private sampleRate = 16000;
  private bitDepth = 16;

  async initialize(options?: PlayerOptions): Promise<void> {
    const sampleRate = options?.sampleRate ?? this.sampleRate;
    const channels = options?.channels ?? this.channels;
    const bitDepth = options?.bitDepth ?? this.bitDepth;

    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate });
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);
    } else if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    this.sampleRate = sampleRate;
    this.channels = channels;
    this.bitDepth = bitDepth;
    this.nextPlaybackTime = this.audioContext?.currentTime ?? 0;

    this.applyGain();
  }

  private applyGain(): void {
    if (this.gainNode) {
      this.gainNode.gain.value = this.muted ? 0 : this.volume;
    }
  }

  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  async suspend(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend();
    }
  }

  /**
   * Play PCM frame (16-bit signed integer, little-endian)
   */
  async playPCMFrame(frame: Uint8Array): Promise<void> {
    if (!this.audioContext || !this.gainNode) {
      await this.initialize();
    }
    const audioContext = this.audioContext;
    const gainNode = this.gainNode;

    if (!audioContext || !gainNode) {
      return;
    }

    // Validate frame is not empty
    if (!frame || frame.length === 0) {
      return;
    }

    try {
      // Convert 16-bit PCM (signed integer, little-endian) to Float32Array
      const samples = this.convertPCMToFloat32(frame);
      const samplesPerChannel = samples.length / this.channels;

      if (samplesPerChannel === 0) {
        return;
      }

      // Create audio buffer
      const buffer = audioContext.createBuffer(
        this.channels,
        samplesPerChannel,
        this.sampleRate,
      );

      // Copy samples to buffer channels
      if (this.channels === 1) {
        // Mono: copy all samples to channel 0
        const channelData = new Float32Array(samples);
        buffer.copyToChannel(channelData, 0, 0);
      } else {
        // Multi-channel: interleaved samples
        for (let ch = 0; ch < this.channels; ch++) {
          const channelData = new Float32Array(samplesPerChannel);
          for (let i = 0; i < samplesPerChannel; i++) {
            channelData[i] = samples[i * this.channels + ch];
          }
          buffer.copyToChannel(channelData, ch, 0);
        }
      }

      // Play the buffer
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNode);

      const startTime = Math.max(this.nextPlaybackTime, audioContext.currentTime);
      source.start(startTime);
      this.nextPlaybackTime = startTime + samplesPerChannel / this.sampleRate;
    } catch (error) {
      console.warn('PCM playback error:', error);
    }
  }

  /**
   * Convert 16-bit PCM (signed integer, little-endian) to Float32Array
   * PCM values are in range [-32768, 32767], normalized to [-1.0, 1.0]
   */
  private convertPCMToFloat32(pcmData: Uint8Array): Float32Array {
    const samples = new Float32Array(pcmData.length / 2);
    const view = new DataView(pcmData.buffer, pcmData.byteOffset, pcmData.byteLength);

    for (let i = 0; i < samples.length; i++) {
      // Read 16-bit signed integer (little-endian)
      const int16 = view.getInt16(i * 2, true);
      // Normalize to [-1.0, 1.0]
      samples[i] = int16 / 32768.0;
    }

    return samples;
  }

  setVolume(value: number): void {
    this.volume = Math.min(Math.max(value, 0), 1);
    this.applyGain();
  }

  setMuted(value: boolean): void {
    this.muted = value;
    this.applyGain();
  }

  getVolume(): number {
    return this.volume;
  }

  isMuted(): boolean {
    return this.muted;
  }

  async reset(): Promise<void> {
    this.nextPlaybackTime = this.audioContext?.currentTime ?? 0;
  }

  async dispose(): Promise<void> {
    await this.suspend();
    this.gainNode?.disconnect();
    this.gainNode = null;
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
  }
}

